import { useCallback, useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { invalidateTenantQueries } from '../lib/mutationHelpers'


import { showToast } from '@/utils/toastMessage'

interface FileUploadEvent {
  xhr: XMLHttpRequest
}

interface UseFileUploadReturn {
  uploadImageCompletedHandler: (
    onChange: (value: string) => void,
    message: string
  ) => (event: FileUploadEvent) => void
  uploadImageErrorHandler: (event: FileUploadEvent) => void
}

export const useFileUpload = (): UseFileUploadReturn => {
  const { tenantId } = useContext(ActiveTenantContext)

  const uploadImageCompletedHandler = useCallback(
    (onChange: (value: string) => void, message: string) => (event: FileUploadEvent) => {
      const logoUrl = event.xhr.responseText
      if (!logoUrl) {
        showToast({
          severity: 'error',
          detail: 'Bild konnte nicht hochgeladen werden. Keine Antwort vom Server.',
        })
        console.error('Upload error! Request data:', event, 'Logo', logoUrl)
        return
      }
      showToast({ severity: 'success', detail: message })
      onChange(JSON.parse(logoUrl))
      invalidateTenantQueries(tenantId)
    },
    [tenantId]
  )

  const uploadImageErrorHandler = useCallback((event: FileUploadEvent) => {
    const { xhr } = event
    let errorMessage = 'Fehler beim Hochladen des Bildes.'

    if (xhr.status === 0) {
      errorMessage = 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.'
    } else if (xhr.status === 401) {
      errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.'
    } else if (xhr.status === 403) {
      errorMessage = 'Zugriff verweigert. Sie haben keine Berechtigung für diese Aktion.'
    } else if (xhr.status === 404) {
      errorMessage = 'Ressource nicht gefunden. Bitte speichern Sie zuerst die Änderungen.'
    } else if (xhr.status === 413) {
      errorMessage = 'Datei ist zu groß. Maximale Größe: 10 MB.'
    } else if (xhr.status === 415) {
      errorMessage = 'Ungültiges Dateiformat. Nur Bilder sind erlaubt.'
    } else if (xhr.status >= 500) {
      errorMessage = 'Serverfehler. Bitte versuchen Sie es später erneut.'
    } else if (xhr.responseText) {
      try {
        const errorData = JSON.parse(xhr.responseText)
        errorMessage = errorData.message || errorData.error || errorMessage
      } catch {
        errorMessage = xhr.responseText || errorMessage
      }
    }

    showToast({
      severity: 'error',
      detail: errorMessage,
    })
    console.error('Upload error!', {
      status: xhr.status,
      statusText: xhr.statusText,
      responseText: xhr.responseText,
      event,
    })
  }, [])

  return {
    uploadImageCompletedHandler,
    uploadImageErrorHandler,
  }
}
